import React, { useMemo } from 'react';
import AddAttachmentMenuItem from './AddAttachmentMenuItem';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import DeleteMenuItem from './DeleteMenuItem';
import DownloadAllMenuItem from './DownloadAllMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import { MenuSeparator } from '../../../components/Menu';
import { isAttachmentUploaded } from '../../../utils/AttachmentUploader';
import useIsPublicView from '../../../utils/useIsPublicView';
import { useDownloadAttachments } from '../../attachment/DownloadAttachmentsProvider';
import type { AttachmentFile } from '../../formats/Attachment';
import Attachment from '../../formats/Attachment';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import type { Blot } from '../../types';
import ConvertIntoMentionMenuItem from '../ConvertIntoMentionMenuItem';
import DetachIntoFileEmbedMenuItem from '../DetachIntoFileEmbedMenuItem';

interface AttachmentToolbarOverflowMenuProps {
  blot: Attachment;
  handleDownloadAll: (() => Promise<void>) | null;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const AttachmentToolbarOverflowMenu = ({
  blot,
  handleDownloadAll,
}: AttachmentToolbarOverflowMenuProps) => {
  const { quill, readOnly } = useQuill();

  const attachmentFiles = blot.getAttachmentFiles();

  return (
    <>
      {quill && !readOnly && (
        <>
          <AddAttachmentMenuItem
            onUpload={(e) => {
              blot.appendAttachment(Array.from(e.currentTarget.files ?? []));
            }}
          />
          <ConvertIntoMentionMenuItem blot={blot} quill={quill} />
          {attachmentFiles.length === 1 && attachmentFiles[0] && (
            <DetachIntoFileEmbedMenuItem
              blot={blot}
              quill={quill}
              source={attachmentFiles[0].source}
              text="Convert into embed"
            />
          )}
        </>
      )}
      {handleDownloadAll && (
        <DownloadAllMenuItem handleDownloadAll={handleDownloadAll} />
      )}
      {!readOnly && (
        <>
          <MenuSeparator />
          <DeleteMenuItem onClick={() => blot.remove()}>
            Delete all files
          </DeleteMenuItem>
        </>
      )}
    </>
  );
};

interface BaseAttachmentToolbarProps {
  hoveredBlot: Attachment;
  handleDownloadAll: (() => Promise<void>) | null;
  setPersist?: (persist: boolean) => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseAttachmentToolbar = ({
  hoveredBlot,
  handleDownloadAll,
  setPersist,
}: BaseAttachmentToolbarProps) => {
  const isPublicView = useIsPublicView();
  const { readOnly } = useQuill();
  useBlotHighlightedClass(hoveredBlot);
  const formats = useMemo(() => hoveredBlot.formats(), [hoveredBlot]);

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        handleDownloadAll || !readOnly ? (
          <ToolbarOverflowMenu onMenuOpenChange={setPersist}>
            <AttachmentToolbarOverflowMenu
              blot={hoveredBlot}
              handleDownloadAll={handleDownloadAll}
            />
          </ToolbarOverflowMenu>
        ) : undefined
      }
    >
      {(!readOnly || !isPublicView) && (
        <ToolbarCommentButtonGroup
          blot={hoveredBlot}
          existingComment={formats.comment}
          onReactionPickerOpenChange={setPersist}
        />
      )}
    </Toolbar>
  );
};

type Context = {
  visibleAttachments: AttachmentFile[];
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Context,
  Attachment
> = {
  isHoveredBlot(blot: Blot) {
    return (
      blot.statics.blotName === Attachment.blotName &&
      blot instanceof Attachment
    );
  },
  getContext(blot: Attachment) {
    return {
      visibleAttachments: blot.getVisibleAttachments(),
    };
  },
};

const AttachmentToolbar = () => {
  const downloadAttachments = useDownloadAttachments();
  const isPublicView = useIsPublicView();
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { visibleAttachments } = {
      visibleAttachments: [],
    },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);

  const isHidden =
    // Attachment upload was failed, or other collaborator is uploading.
    !visibleAttachments.length ||
    (isPublicView && !downloadableAttachments.length);

  const handleDownloadAll =
    downloadAttachments && downloadableAttachments.length > 0
      ? () => downloadAttachments(downloadableAttachments)
      : null;

  return (
    <FloatingToolbar.Toolbar
      testId="attachmentToolbar"
      bounds={isHidden ? null : bounds}
    >
      {hoveredBlot instanceof Attachment && (
        <BaseAttachmentToolbar
          hoveredBlot={hoveredBlot}
          handleDownloadAll={handleDownloadAll}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default AttachmentToolbar;
